import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { PageHeader, LoadingSkeleton } from "@/components/ui-system";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, Loader2, Save, ChevronUp, ChevronDown, Trash2, Plus, Truck } from "lucide-react";

/**
 * Admin: the ordered "EdgeLMS Recommended" courier fallback list. The top
 * courier that can service a lane is the one shown to sellers — so we never
 * depend on a single courier. (Manual until the smart engine ships.)
 */
const B2CRecommendationEngine = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [couriers, setCouriers] = useState([]);
  const [order, setOrder] = useState([]); // selected courier codes, priority order
  const [addCode, setAddCode] = useState("");

  const byCode = useMemo(() => Object.fromEntries(couriers.map((c) => [c.code, c])), [couriers]);
  const unselected = useMemo(() => couriers.filter((c) => !order.includes(c.code)), [couriers, order]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rec, cour] = await Promise.all([
        axios.get(`${API}/b2c/admin/recommendation`),
        axios.get(`${API}/b2c/admin/couriers`),
      ]);
      const all = (cour.data || []).filter((c) => c.active !== false);
      setCouriers(all);
      setOrder((rec.data?.priority || []).filter((code) => all.some((c) => c.code === code)));
    } catch (e) {
      toast.error("Failed to load recommendation config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const move = (idx, dir) =>
    setOrder((prev) => {
      const j = idx + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  const remove = (code) => setOrder((prev) => prev.filter((c) => c !== code));
  const add = () => {
    if (addCode && !order.includes(addCode)) {
      setOrder((prev) => [...prev, addCode]);
      setAddCode("");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/b2c/admin/recommendation`, { priority: order });
      toast.success("Recommended courier priority saved");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="max-w-2xl space-y-6">
        <PageHeader
          title="B2C Courier Recommendation Engine"
          subtitle="The ordered list of couriers EdgeLMS recommends to sellers. The top courier that can service a lane becomes their 'EdgeLMS Recommended' — manual for now; the smart engine is coming."
        />
        <Card>
          <CardContent className="space-y-4 p-5">
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="reco-placeholder-note">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0" />
              Placeholder: until the recommendation engine ships, this fallback list drives the "EdgeLMS Recommended"
              option sellers see — the highest-ranked courier that can service the lane wins, so we never depend on one courier.
            </div>

            {loading ? (
              <LoadingSkeleton variant="table" rows={3} />
            ) : (
              <>
                {order.length === 0 ? (
                  <p className="rounded-lg border border-dashed p-4 text-sm text-slate-500" data-testid="reco-empty">
                    No recommended couriers set yet. Add couriers below to build the fallback order (1 = first choice).
                  </p>
                ) : (
                  <div className="divide-y rounded-lg border">
                    {order.map((code, idx) => {
                      const c = byCode[code];
                      return (
                        <div key={code} className="flex items-center gap-2 px-3 py-2" data-testid={`reco-row-${code}`}>
                          <span className="w-6 text-center text-sm font-semibold tabular-nums text-slate-400">{idx + 1}</span>
                          {c?.logo_url ? (
                            <img src={c.logo_url} alt="" className="h-7 w-7 rounded border bg-white object-contain p-0.5" />
                          ) : (
                            <span className="flex h-7 w-7 items-center justify-center rounded bg-slate-100 text-slate-400"><Truck className="h-4 w-4" /></span>
                          )}
                          <span className="flex-1 truncate text-sm font-medium text-slate-700">{c?.name || code}</span>
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={idx === 0} onClick={() => move(idx, -1)} data-testid={`reco-up-${code}`}>
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={idx === order.length - 1} onClick={() => move(idx, 1)} data-testid={`reco-down-${code}`}>
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => remove(code)} data-testid={`reco-remove-${code}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <Select value={addCode} onValueChange={setAddCode}>
                    <SelectTrigger className="flex-1" data-testid="reco-add-select">
                      <SelectValue placeholder={unselected.length ? "Add a courier…" : "All couriers added"} />
                    </SelectTrigger>
                    <SelectContent>
                      {unselected.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.name} ({c.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" onClick={add} disabled={!addCode} data-testid="reco-add-btn">
                    <Plus className="mr-1.5 h-4 w-4" /> Add
                  </Button>
                </div>

                <div className="flex justify-end">
                  <Button onClick={save} disabled={saving} data-testid="reco-save">
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Save priority
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default B2CRecommendationEngine;
